import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import Swal from "sweetalert2"

export default class extends Controller {
  static targets = ["form", "filters", "drawer"]

  connect() {
    this.pageNumber = new URL(window.location.href).searchParams.get("page") || "1"
    this.changed = () => {
      if (document.visibilityState === "hidden") return
      clearTimeout(this.liveTimer)
      this.liveTimer = setTimeout(() => this.refresh(false), 600)
    }
    this.visibilityChanged = () => { if (document.visibilityState === "visible") this.changed() }
    document.addEventListener("admin-campaigns:changed", this.changed)
    document.addEventListener("visibilitychange", this.visibilityChanged)
    this.stream = this.element.querySelector("#admin-campaign-stream")
    if (this.stream) {
      this.streamObserver = new MutationObserver(() => { if (this.stream.hasAttribute("connected")) this.changed() })
      this.streamObserver.observe(this.stream, { attributes: true, attributeFilter: ["connected"] })
      if (this.stream.hasAttribute("connected")) this.changed()
    }
    const drawer = this.hasDrawerTarget && this.drawerTarget.querySelector("aside")
    if (drawer) {
      this.drawerUrl = window.location.href
      document.body.classList.add("admin-campaign-overlay-open")
      drawer.querySelector("header button")?.focus()
    }
  }

  disconnect() {
    clearTimeout(this.liveTimer)
    clearTimeout(this.filterTimer)
    this.request?.abort()
    this.drawerRequest?.abort()
    this.streamObserver?.disconnect()
    document.removeEventListener("admin-campaigns:changed", this.changed)
    document.removeEventListener("visibilitychange", this.visibilityChanged)
    document.body.classList.remove("admin-campaign-overlay-open")
  }

  queueFilter(event) {
    if (event.isComposing) return
    clearTimeout(this.filterTimer)
    this.filterTimer = setTimeout(() => this.filter(), 300)
  }

  filter(event) {
    event?.preventDefault()
    clearTimeout(this.filterTimer)
    this.pageNumber = "1"
    this.refresh(true)
  }

  status(event) {
    event.preventDefault()
    this.formTarget.elements.status.value = event.currentTarget.dataset.status
    this.filter()
  }

  page(event) {
    event.preventDefault()
    this.pageNumber = new URL(event.currentTarget.href).searchParams.get("page") || "1"
    this.refresh(true)
  }

  resetFilters() {
    for (const field of this.formTarget.elements) {
      if (field.name === "q") field.value = ""
      else if (field.name === "sort") field.value = "recent"
      else if (field.name === "status") field.value = "all"
      else if (field.tagName === "SELECT") field.value = "any"
    }
    this.filter()
  }

  toggleFilters(event) {
    this.filtersTarget.hidden = !this.filtersTarget.hidden
    event.currentTarget.setAttribute("aria-expanded", String(!this.filtersTarget.hidden))
  }

  reportUrl() {
    const url = new URL(this.formTarget.action)
    for (const [key, value] of new FormData(this.formTarget)) {
      if (value && value !== "any" && !(key === "sort" && value === "recent") && !(key === "status" && value === "all")) url.searchParams.set(key, value)
    }
    if (Number(this.pageNumber) > 1) url.searchParams.set("page", this.pageNumber)
    return url
  }

  async refresh(userInitiated) {
    if (!this.hasFormTarget) {
      if (this.drawerUrl) await this.loadDrawer(this.drawerUrl, false)
      return
    }
    this.request?.abort()
    const request = new AbortController()
    this.request = request
    const url = this.reportUrl()
    try {
      const html = await this.fetchHtml(url, request.signal)
      if (request.signal.aborted || !this.element.isConnected) return
      this.replaceSections(html, ["admin-campaign-overview", "admin-campaign-results"])
      if (userInitiated) history.replaceState(history.state, "", `${url.pathname}${url.search}`)
      if (this.drawerUrl) await this.loadDrawer(this.drawerUrl, false)
    } catch (error) { this.showError(error, userInitiated) }
  }

  open(event) {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
    event.preventDefault()
    if (!this.drawerTarget.contains(event.currentTarget)) this.opener = event.currentTarget
    this.loadDrawer(event.currentTarget.href, true)
  }

  drawerFilter(event) {
    event.preventDefault()
    this.loadDrawer(event.currentTarget.href, true)
  }

  async loadDrawer(url, userInitiated) {
    this.drawerRequest?.abort()
    const request = new AbortController()
    this.drawerRequest = request
    try {
      const html = await this.fetchHtml(url, request.signal)
      if (request.signal.aborted || !this.element.isConnected) return
      const existing = this.drawerTarget.querySelector("aside")
      const isSame = existing && new URL(existing.dataset.campaignUrl, location.origin).pathname === new URL(url, location.origin).pathname
      if (isSame) this.replaceSections(html, ["admin-campaign-drawer-header", "admin-campaign-drawer-content"])
      else this.drawerTarget.innerHTML = html
      this.drawerUrl = url
      document.body.classList.add("admin-campaign-overlay-open")
      if (!isSame) this.drawerTarget.querySelector("header button")?.focus()
    } catch (error) { this.showError(error, userInitiated) }
  }

  close(event) {
    event?.preventDefault()
    if (event?.type === "keydown") {
      const composerClose = this.element.querySelector(".admin-marketing-composer [aria-label='Close composer']")
      if (composerClose) { composerClose.click(); return }
    }
    this.drawerRequest?.abort()
    this.drawerUrl = null
    if (this.hasDrawerTarget) this.drawerTarget.innerHTML = ""
    document.body.classList.remove("admin-campaign-overlay-open")
    this.opener?.focus()
  }

  composerOpened(event) {
    const composer = event.target.querySelector(".admin-marketing-composer")
    if (!composer) return
    this.close()
    composer.querySelector("[aria-label='Close composer']")?.focus()
  }

  trapFocus(event) {
    const drawer = this.element.querySelector(".admin-marketing-composer") ||
      (this.hasDrawerTarget && this.drawerTarget.querySelector("aside"))
    if (!drawer) return
    const focusable = Array.from(drawer.querySelectorAll("a[href], button:not([disabled]), summary, input, select, textarea, [tabindex='0']")).filter(node => node.getClientRects().length)
    const first = focusable[0], last = focusable[focusable.length - 1]
    if (event.shiftKey && (document.activeElement === first || !drawer.contains(document.activeElement))) {
      event.preventDefault()
      last?.focus()
    } else if (!event.shiftKey && (document.activeElement === last || !drawer.contains(document.activeElement))) {
      event.preventDefault()
      first?.focus()
    }
  }

  async fetchHtml(url, signal) {
    const response = await fetch(url, { signal, headers: { Accept: "text/html", "X-Campaign-Live": "true" }, cache: "no-store" })
    if (!response.ok || response.redirected) throw new Error("The campaign report could not be refreshed. Please try again, or sign in again if your session expired.")
    return response.text()
  }

  replaceSections(html, ids) {
    const doc = new DOMParser().parseFromString(html, "text/html")
    for (const id of ids) {
      const replacement = doc.getElementById(id)
      if (replacement) Turbo.renderStreamMessage(`<turbo-stream action="replace" method="morph" target="${id}"><template>${replacement.outerHTML}</template></turbo-stream>`)
    }
  }

  showError(error, userInitiated) {
    if (error.name === "AbortError" || !userInitiated) return
    Swal.fire({ icon: "error", title: "Unable to load campaigns", text: error.message })
  }
}
